import { EditNote as EditNoteIcon } from '@mui/icons-material'
import { Alert, Button, MenuItem, Stack } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import MuiMultiSelectField from '@/components/Forms/MuiMultiSelectField'
import MuiSelectField from '@/components/Forms/MuiSelectField'
import FormStack from '@/components/UI/FormContainer'
import { genericErrorMessage } from '@/utils/error'
import type { Squad } from '@/utils/squad'

type Fields = {
  skillLevel: string
  squads: Squad[]
}

export type Props = {
  onSaveSettings: (fields: Fields) => Promise<any>
  skillLevel?: number
  squads: Squad[]
}

const AdminSettingsForm: FC<Props> = ({ onSaveSettings, skillLevel, squads }) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<Fields>({
    defaultValues: {
      skillLevel: skillLevel?.toString() ?? '1',
      squads,
    },
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)

  useEffect(() => {
    const subscription = watch(() => {
      setIsSuccess(false)
      setErrorMessage(undefined)
    })

    return () => subscription.unsubscribe()
  }, [watch])

  const onSubmit: SubmitHandler<Fields> = async data => {
    setIsLoading(true)
    setErrorMessage(undefined)
    setIsSuccess(false)

    try {
      await onSaveSettings(data)

      setIsSuccess(true)
    } catch (e) {
      setErrorMessage(genericErrorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormStack width={600}>
        <MuiSelectField
          id="skillLevel"
          name="skillLevel"
          label="Skill level"
          control={control}
        >
          <MenuItem value="1">1</MenuItem>
          <MenuItem value="2">2</MenuItem>
          <MenuItem value="3">3</MenuItem>
          <MenuItem value="4">4</MenuItem>
          <MenuItem value="5">5</MenuItem>
          <MenuItem value="6">6</MenuItem>
          <MenuItem value="7">7</MenuItem>
        </MuiSelectField>
        <MuiMultiSelectField
          id="squads"
          name="squads"
          label="Squads"
          rules={{ required: true }}
          control={control}
          options={[
            { label: 'A', value: 'a' },
            { label: 'B', value: 'b' },
            { label: 'C', value: 'c' },
            { label: 'D', value: 'd' },
            { label: 'E', value: 'e' },
            { label: 'G', value: 'g' },
            { label: 'I', value: 'i' },
            { label: 'J', value: 'j' },
            { label: 'K', value: 'k' },
            { label: 'L', value: 'l' },
          ]}
        />
        <Stack alignItems="end">
          <Button
            type="submit"
            name="create"
            variant="contained"
            disabled={!isValid}
            loading={isLoading}
            sx={{ width: 200 }}
            loadingPosition="end"
            endIcon={<EditNoteIcon />}
          >
            Update
          </Button>
        </Stack>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {isSuccess && <Alert severity="info">Successfully updated.</Alert>}
      </FormStack>
    </form>
  )
}

export default AdminSettingsForm
